'use client'

import { useEffect } from 'react'
import { useTranslations } from 'next-intl'

import { Button, Heading, Icon, IconComponent, Paragraph } from '@graftcode/design-system'

import { steps } from './ProjectCreatedSuccess.const'
import { fireRealisticConfetti } from './ProjectCreatedSuccess.helpers'
import type { Props } from './ProjectCreatedSuccess.types'

import styles from './ProjectCreatedSuccess.module.scss'

export function ProjectCreatedSuccess({ workspaceName, onContinue }: Props) {
  const t = useTranslations('projectCreatedSuccess')

  useEffect(() => {
    fireRealisticConfetti()
  }, [])

  return (
    <div className={styles.wrapper}>
      <div className={styles.content}>
        <div className={styles.checkmark}>
          <IconComponent icon={Icon.CheckmarkRegular} />
        </div>

        <Heading type="h3" className={styles.title}>
          {t('title')}
        </Heading>

        <Paragraph size="l" className={styles.description}>
          {t('description', { workspaceName })}
        </Paragraph>

        <div className={styles.nextSteps}>
          <Paragraph size="l" weight="semi-bold" className={styles.nextStepsLabel}>
            {t('nextStepsLabel')}
          </Paragraph>

          <div className={styles.stepsList}>
            {steps.map((step) => (
              <div
                key={step.key}
                className={`${styles.stepItem} ${step.highlighted ? styles.stepItemHighlighted : ''} ${step.disabled ? styles.stepItemDisabled : ''}`}
                onClick={step.onClick}
                role={step.onClick ? 'button' : undefined}
                tabIndex={step.onClick ? 0 : undefined}
              >
                <div className={`${styles.stepIconWrapper} ${step.iconClass}`}>
                  <IconComponent icon={step.icon} />
                </div>

                <div className={styles.stepText}>
                  <Paragraph size="sm" weight="semi-bold" className={styles.stepTitle}>
                    {t(`steps.${step.key}.title`)}
                  </Paragraph>
                  <Paragraph size="sm" className={styles.stepDescription}>
                    {t(`steps.${step.key}.description`)}
                  </Paragraph>
                </div>

                <div className={styles.stepArrow}>
                  <IconComponent icon={Icon.ChevronRightRegular} />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className={styles.actions}>
        <Button
          type="button"
          variant="primary"
          size="l"
          data-testid="project-created-success-continue"
          onClick={onContinue}
        >
          {t('continueButton')}
        </Button>
      </div>
    </div>
  )
}
